import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, TextField, Typography, FormControl, InputLabel, Select, MenuItem, IconButton as MuiIconButton } from "@mui/material";
import { Add } from "@mui/icons-material";
import { THEME_MAP } from "../theme";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useStateRegions, useStateRegionActions } from "../actions/stateRegions";
import { firestore, functions } from "../services/firebase";
import { useIsMobile } from "../hooks/useIsMobile";
import { captureError } from "../lib/errorReporting";

export function Regions() {
	const states = useStateRegions();
	const {
		fetchStateRegions,
		saveRegion,
		deleteRegion,
		saveSubregion,
		deleteSubregion
	} = useStateRegionActions();

	const [selectedStateId, setSelectedStateId] = useState("");
	const [selectedRegionId, setSelectedRegionId] = useState("");

	const [regionModalOpen, setRegionModalOpen] = useState(false);
	const [subregionModalOpen, setSubregionModalOpen] = useState(false);

	const [regionDraft, setRegionDraft] = useState({});
	const [subregionDraft, setSubregionDraft] = useState({});
	const [legacyRegions, setLegacyRegions] = useState([]);
	const [legacySubregionId, setLegacySubregionId] = useState("");
	const [legacyStateId, setLegacyStateId] = useState("");
	const [legacyRegionId, setLegacyRegionId] = useState("");
	const [legacySaving, setLegacySaving] = useState(false);
	const [legacyStatus, setLegacyStatus] = useState(null);
	const isMobile = useIsMobile();
	const assignLegacySubregionParent = useMemo(
		() => functions.httpsCallable("assignLegacySubregionParent"),
		[]
	);

	const selectedState = useMemo(
		() => states.find((state) => state.id === selectedStateId),
		[states, selectedStateId]
	);
	const selectedRegion = useMemo(
		() => selectedState?.regions?.find((region) => region.id === selectedRegionId),
		[selectedRegionId, selectedState]
	);
	const selectedLegacySubregion = useMemo(
		() => legacyRegions.find((region) => region.id === legacySubregionId),
		[legacyRegions, legacySubregionId]
	);
	const selectedLegacyState = useMemo(
		() => states.find((state) => state.id === legacyStateId),
		[states, legacyStateId]
	);

	useEffect(() => {
		fetchStateRegions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		firestore
			.collection("Regions")
			.get()
			.then((snapshot) => {
				const rows = [];
				snapshot.forEach((doc) => {
					const data = doc.data() || {};
					if (data.migrated_to_new_region_structure === true) {
						return;
					}
					rows.push({ ...data, id: doc.id });
				});
				rows.sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
				setLegacyRegions(rows);
			})
			.catch((error) => {
				captureError(error, { view: "Regions", action: "loadLegacySubregions" });
				console.error("Failed to load legacy subregions", error);
			});
	}, []);

	useEffect(() => {
		if (!selectedStateId && states.length > 0) {
			const michigan = states.find(
				(state) =>
					(state.name || "").toLowerCase() === "michigan" ||
					(state.abbr || "").toLowerCase() === "mi"
			);
			setSelectedStateId(michigan?.id || states[0].id);
		}
	}, [selectedStateId, states]);

	useEffect(() => {
		if (!selectedRegionId && selectedState?.regions?.length) {
			setSelectedRegionId(selectedState.regions[0].id);
		}
	}, [selectedRegionId, selectedState]);

	const handleCreateRegion = useCallback(() => {
		setRegionDraft({});
		setRegionModalOpen(true);
	}, []);

	const handleEditRegion = useCallback((region) => {
		setRegionDraft(region);
		setRegionModalOpen(true);
	}, []);

	const handleCreateSubregion = useCallback(() => {
		setSubregionDraft({});
		setSubregionModalOpen(true);
	}, []);

	const handleEditSubregion = useCallback((subregion) => {
		setSubregionDraft(subregion);
		setSubregionModalOpen(true);
	}, []);

	const handleLegacyCancel = useCallback(() => {
		setLegacySubregionId("");
		setLegacyStateId("");
		setLegacyRegionId("");
		setLegacyStatus(null);
	}, []);

	const handleLegacySave = useCallback(async () => {
		if (!selectedLegacySubregion || !legacyStateId || !legacyRegionId) {
			return;
		}
		setLegacyStatus(null);
		setLegacySaving(true);
		try {
			const legacyName = selectedLegacySubregion.Name || selectedLegacySubregion.name || "";
			await saveSubregion(legacyStateId, legacyRegionId, selectedLegacySubregion.id, {
				id: selectedLegacySubregion.id,
				name: legacyName
			});

			const response = await assignLegacySubregionParent({
				subregionId: selectedLegacySubregion.id,
				stateId: legacyStateId,
				regionId: legacyRegionId
			});

			await firestore
				.collection("Regions")
				.doc(selectedLegacySubregion.id)
				.set({ migrated_to_new_region_structure: true }, { merge: true });
			setLegacyRegions((current) =>
				current.filter((region) => region.id !== selectedLegacySubregion.id)
			);
			const updatedCount = response?.data?.updated ?? 0;
			const stateLabel = selectedLegacyState?.name || legacyStateId;
			const regionLabel =
				selectedLegacyState?.regions?.find((region) => region.id === legacyRegionId)?.name ||
				legacyRegionId;
			const subregionLabel = legacyName || selectedLegacySubregion.id;
			setLegacyStatus({
				type: "success",
				message: `Adding ${subregionLabel} to the ${regionLabel} region in ${stateLabel} was successful. (${updatedCount} bars updated)`
			});
			setLegacySubregionId("");
			setLegacyStateId("");
			setLegacyRegionId("");
		} catch (error) {
			captureError(error, { view: "Regions", action: "assignLegacySubregion" });
			console.error("Failed to assign legacy subregion", error);
			setLegacyStatus({
				type: "error",
				message: "Adding the subregion was NOT successful. Please try again."
			});
		} finally {
			setLegacySaving(false);
		}
	}, [assignLegacySubregionParent, legacyRegionId, legacyStateId, saveSubregion, selectedLegacyState, selectedLegacySubregion]);

	return (
		<Container>
			<HeaderRow>
				<div>
					<Typography variant="h6">State → Region → Subregion</Typography>
					<Typography variant="body2" color="textSecondary">
						Assign regions and subregions per state. Subregions are the values assigned to bars.
					</Typography>
				</div>
			</HeaderRow>

			<StateSelector>
				<FormControl size="small" variant="outlined" className="state-control">
					<InputLabel id="state-select-label">State</InputLabel>
					<Select
						labelId="state-select-label"
						label="State"
						value={selectedStateId}
						onChange={(event) => {
							setSelectedStateId(event.target.value);
							setSelectedRegionId("");
						}}
					>
						{states.map((state) => (
							<MenuItem key={state.id} value={state.id}>
								{state.name}{state.abbr ? ` (${state.abbr})` : ""}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Typography variant="subtitle1" className="state-title">
					{selectedState?.name || "Select a state"}
				</Typography>
			</StateSelector>

			<Columns>
				<Column>
					<ColumnHeader>
						<Typography variant="subtitle1">Regions</Typography>
						{isMobile ? (
							<MuiIconButton
								size="small"
								disabled={!selectedStateId}
								onClick={handleCreateRegion}
								sx={{ color: "#1976d2" }}
							>
								<Add />
							</MuiIconButton>
						) : (
							<Button
								size="small"
								variant="outlined"
								disabled={!selectedStateId}
								onClick={handleCreateRegion}
							>
								Create Region
							</Button>
						)}
					</ColumnHeader>
					<ListContainer>
						{(selectedState?.regions || []).map((region) => (
							<ListItem
								key={region.id}
								className={region.id === selectedRegionId ? "active" : ""}
								onClick={() => setSelectedRegionId(region.id)}
							>
								<div>
									<span className="title">{region.name || "Unnamed Region"}</span>
									<span className="subtitle">{(region.subregions || []).length} subregions</span>
								</div>
								<Button
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										handleEditRegion(region);
									}}
								>
									Edit
								</Button>
							</ListItem>
						))}
						{selectedState && (selectedState.regions || []).length === 0 && (
							<EmptyState>No regions yet for this state.</EmptyState>
						)}
						{!selectedState && (
							<EmptyState>Select a state to manage regions.</EmptyState>
						)}
					</ListContainer>
				</Column>

				<Column>
					<ColumnHeader>
						<Typography variant="subtitle1">Subregions</Typography>
						{isMobile ? (
							<MuiIconButton
								size="small"
								disabled={!selectedStateId || !selectedRegionId}
								onClick={handleCreateSubregion}
								sx={{ color: "#1976d2" }}
							>
								<Add />
							</MuiIconButton>
						) : (
							<Button
								size="small"
								variant="outlined"
								disabled={!selectedStateId || !selectedRegionId}
								onClick={handleCreateSubregion}
							>
								Create Subregion
							</Button>
						)}
					</ColumnHeader>
					<ListContainer>
						{(selectedRegion?.subregions || []).map((subregion) => (
							<ListItem key={subregion.id}>
								<div>
									<span className="title">{subregion.name || "Unnamed Subregion"}</span>
								</div>
								<Button
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										handleEditSubregion(subregion);
									}}
								>
									Edit
								</Button>
							</ListItem>
						))}
						{selectedRegion && (selectedRegion.subregions || []).length === 0 && (
							<EmptyState>No subregions yet for this region.</EmptyState>
						)}
						{!selectedRegion && (
							<EmptyState>Select a region to manage subregions.</EmptyState>
						)}
					</ListContainer>
				</Column>
			</Columns>

			<LegacyContainer>
				<div className="header">
					<Typography variant="h6">Unassigned Subregions (Legacy)</Typography>
					<Typography variant="body2" color="textSecondary">
						Add these subregions to a region belonging to a state. The region for the state must exist before you assign them.
					</Typography>
				</div>

				<LegacyColumns>
					<div className="column">
						<Typography variant="subtitle2">Pick the unassigned region from the list</Typography>
						<FormControl size="small" variant="outlined" fullWidth>
							<InputLabel id="legacy-subregion-select-label">Unassigned Subregion</InputLabel>
							<Select
								labelId="legacy-subregion-select-label"
								label="Unassigned Subregion"
								value={legacySubregionId}
								onChange={(event) => setLegacySubregionId(event.target.value)}
							>
								<MenuItem value="">Select Subregion</MenuItem>
								{legacyRegions.map((region) => (
									<MenuItem key={region.id} value={region.id}>
										{region.Name || region.name || region.id}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>

					<div className="column">
						<Typography variant="subtitle2">Now select which state it belongs to</Typography>
						<FormControl size="small" variant="outlined" fullWidth>
							<InputLabel id="legacy-state-select-label">State</InputLabel>
							<Select
								labelId="legacy-state-select-label"
								label="State"
								value={legacyStateId}
								onChange={(event) => {
									setLegacyStateId(event.target.value);
									setLegacyRegionId("");
								}}
								disabled={!legacySubregionId}
							>
								<MenuItem value="">Select State</MenuItem>
								{states.map((state) => (
									<MenuItem key={state.id} value={state.id}>
										{state.name}{state.abbr ? ` (${state.abbr})` : ""}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>

					<div className="column">
						<Typography variant="subtitle2">
							Now select the region in the state that {selectedLegacySubregion?.Name || selectedLegacySubregion?.name || "this subregion"} belongs to
						</Typography>
						<FormControl size="small" variant="outlined" fullWidth>
							<InputLabel id="legacy-region-select-label">Region</InputLabel>
							<Select
								labelId="legacy-region-select-label"
								label="Region"
								value={legacyRegionId}
								onChange={(event) => setLegacyRegionId(event.target.value)}
								disabled={!legacyStateId}
							>
								<MenuItem value="">Select Region</MenuItem>
								{(selectedLegacyState?.regions || []).map((region) => (
									<MenuItem key={region.id} value={region.id}>
										{region.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
				</LegacyColumns>

				<div className="actions">
					<div className="status">
						<Typography variant="body2" color="textSecondary">
							Saving may take a minute as we update the bars using this sub-region.
						</Typography>
						{legacyStatus && (
							<Typography
								variant="body2"
								className={legacyStatus.type === "error" ? "status-error" : "status-success"}
							>
								{legacyStatus.message}
							</Typography>
						)}
					</div>
					<Button variant="outlined" onClick={handleLegacyCancel} disabled={legacySaving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleLegacySave}
						disabled={legacySaving || !legacySubregionId || !legacyStateId || !legacyRegionId}
					>
						{legacySaving ? "Saving..." : "Save"}
					</Button>
				</div>
			</LegacyContainer>

			<Dialog open={regionModalOpen} onClose={() => setRegionModalOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>{regionDraft?.id ? "Edit Region" : "Create Region"}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Region Name"
						value={regionDraft?.name || ""}
						onChange={(event) => setRegionDraft((current) => ({ ...current, name: event.target.value }))}
						fullWidth
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRegionModalOpen(false)}>Cancel</Button>
					{regionDraft?.id && (
						<Button
							onClick={() => {
								// eslint-disable-next-line no-restricted-globals
								if (confirm("Are you sure? This will delete the region and its subregions.")) {
									deleteRegion(selectedStateId, regionDraft.id).then(() => {
										setRegionModalOpen(false);
										setSelectedRegionId("");
										fetchStateRegions();
									}).catch((err) => {
										captureError(err, { view: "Regions", action: "deleteRegion" });
										console.error("Failed to delete region", err);
									});
								}
							}}
							color="error"
						>
							Delete
						</Button>
					)}
					<Button
						onClick={() =>
							saveRegion(selectedStateId, regionDraft?.id || "new", regionDraft).then(() => {
								setRegionModalOpen(false);
								fetchStateRegions();
							}).catch((err) => {
								captureError(err, { view: "Regions", action: "saveRegion" });
								console.error("Failed to save region", err);
							})
						}
						color="primary"
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={subregionModalOpen} onClose={() => setSubregionModalOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>{subregionDraft?.id ? "Edit Subregion" : "Create Subregion"}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Subregion Name"
						value={subregionDraft?.name || ""}
						onChange={(event) => setSubregionDraft((current) => ({ ...current, name: event.target.value }))}
						fullWidth
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSubregionModalOpen(false)}>Cancel</Button>
					{subregionDraft?.id && (
						<Button
							onClick={() => {
								// eslint-disable-next-line no-restricted-globals
								if (confirm("Are you sure? This will delete the subregion.")) {
									deleteSubregion(selectedStateId, selectedRegionId, subregionDraft.id).then(() => {
										setSubregionModalOpen(false);
										fetchStateRegions();
									}).catch((err) => {
										captureError(err, { view: "Regions", action: "deleteSubregion" });
										console.error("Failed to delete subregion", err);
									});
								}
							}}
							color="error"
						>
							Delete
						</Button>
					)}
					<Button
						onClick={() =>
							saveSubregion(selectedStateId, selectedRegionId, subregionDraft?.id || "new", subregionDraft).then(() => {
								setSubregionModalOpen(false);
								fetchStateRegions();
							}).catch((err) => {
								captureError(err, { view: "Regions", action: "saveSubregion" });
								console.error("Failed to save subregion", err);
							})
						}
						color="primary"
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;
	overflow: auto;
	padding: 24px;
	background: ${THEME_MAP["color-basic-100"]};
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 24px;
	flex-wrap: wrap;
`;

const StateSelector = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
	flex-wrap: wrap;

	.state-control {
		min-width: 260px;
		background: #fff;
	}

	.state-title {
		font-weight: 600;
		color: ${THEME_MAP["color-basic-800"]};
	}
`;

const Columns = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(280px, 1fr));
	gap: 16px;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const LegacyContainer = styled.div`
	margin-top: 24px;
	background: #fff;
	border-radius: 8px;
	border: 1px solid ${THEME_MAP["color-basic-300"]};
	padding: 16px 20px;

	.header {
		margin-bottom: 16px;
	}

	.actions {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
		margin-top: 16px;
		align-items: flex-start;

		.status {
			flex: 1;
			padding-right: 12px;
			min-width: 200px;
			display: flex;
			flex-direction: column;
			gap: 6px;
		}

		.status-success {
			color: ${THEME_MAP["color-success-600"]};
		}

		.status-error {
			color: ${THEME_MAP["color-danger-600"]};
		}
	}
`;

const LegacyColumns = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 16px;

	.column {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
`;

const Column = styled.div`
	background: #fff;
	border-radius: 8px;
	border: 1px solid ${THEME_MAP["color-basic-300"]};
	min-height: 420px;
	display: flex;
	flex-direction: column;
`;

const ColumnHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border-bottom: 1px solid ${THEME_MAP["color-basic-200"]};
	background: ${THEME_MAP["color-basic-200"]};
	border-radius: 8px 8px 0 0;

	.MuiTypography-subtitle1 {
		font-weight: 600;
	}
`;

const ListContainer = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 8px 0;
`;

const ListItem = styled.div`
	padding: 10px 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	cursor: pointer;

	&.active {
		background: ${THEME_MAP["color-primary-transparent-100"]};
	}

	&:hover {
		background: ${THEME_MAP["color-basic-200"]};
	}

	span.title {
		font-weight: 400;
		display: block;
	}

	span.subtitle {
		font-size: 0.85rem;
		color: ${THEME_MAP["color-basic-600"]};
	}
`;

const EmptyState = styled.div`
	padding: 16px;
	color: ${THEME_MAP["color-basic-600"]};
	font-size: 0.9rem;
`;
